<template>
  <app-modal v-model="isDialogOpened"
             hide-header
             size="lg"
             @cancel="closeDialog"
  >
    <template #body>
      <ProjectTypeFeatureHeader :shadow-color="PROJECT_TYPE_COLORS.PROJECT"
                                :illustration="contactClosdIllustration"
                                @close="closeDialog"
      >
        <template #title>
          <h3 class="text-light-blue">{{ $t('projects.dialogs.ProjectTypeQuoteRequestDialog.contactUs') }}</h3>
          <h1>{{ $t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequest') }}</h1>
        </template>
      </ProjectTypeFeatureHeader>

      <div class="body">
        <div class="copyright">
          <CopyrightLink author="pch.vector"
                         link="https://fr.freepik.com/vecteurs-libre/document-signature-partenaires-commerciaux-petits-personnages-stylo-papier-signature-sceau-plat-illustration_11235250.htm"
          />
        </div>

        <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.text" tag="p" scope="global">
          <template #phoneNumber>
            <a :href="`tel:${SALES_PHONE_NUMBER_WITH_COUNTRY_CODE}`" class="phone-number body-md-bold">{{ SALES_PHONE_NUMBER }}</a>
          </template>
        </i18n-t>

        <p class="mt-4 heading-xs-bold required-asterisk">{{ $t('projects.dialogs.ProjectTypeQuoteRequestDialog.phoneNumberLabel') }}</p>
        <AppPhoneField :value="telephone"
                       class="my-6"
                       :error-messages="phoneNumberErrors"
                       @on-input="handlePhoneChange"
        />

        <p class="mt-4 heading-xs-bold">{{$t('projects.dialogs.ProjectTypeQuoteRequestDialog.whichProjectTypeQuestion')}}</p>

        <AppSelect v-model="v$.quoteRelatedProjectType.$model"
                   :items="projectTypes"
                   class="projet-type-select my-6"
                   no-label
        >
          <template #selection="{item}">
            <div class="d-flex align-center">
              <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.projectTypeSelection" tag="p" class="mb-0" scope="global">
                <template #projectTypeName>
                  <span class="body-md-bold">{{item.title}}</span>
                </template>
              </i18n-t>
            </div>
          </template>
          <template #item="{item, props}">
            <v-list-item v-bind="props">
              <template #title>
                <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.projectTypeSelection" tag="p" class="mb-0" scope="global">
                  <template #projectTypeName>
                    <span class="body-md-bold">{{item.title}}</span>
                  </template>
                </i18n-t>
              </template>
            </v-list-item>
          </template>
        </AppSelect>

        <v-textarea v-model="v$.requestDetails.$model"
                    hide-details
                    color="blue-darken-2"
                    no-resize
                    variant="outlined"
                    :placeholder="$t('projects.dialogs.ProjectTypeQuoteRequestDialog.requestMessagePlaceholder')"
                    rows="4"
        />

        <v-checkbox v-model="v$.isPrivacyPolicyCheck.$model"
                    hide-details
                    class="privacy-policy-check"
        >
          <template #label>
            <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyConsentText" tag="p" class="mb-0" scope="global">
              <template #privacyPolicyUrl>
                <a :href="$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyUrl')"
                   target="_blank"
                   class="body-md-bold d-inline-block text-text-link"
                   @click.stop
                >
                  {{$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicy')}}
                </a>
              </template>
            </i18n-t>
          </template>
        </v-checkbox>
        <p class="detail-md-regular mt-4">* {{$t('projects.dialogs.ProjectTypeQuoteRequestDialog.mandatoryFields')}}</p>
      </div>
      <!-- Exists purely to avoid reloading after submitting the form... -->
      <iframe class="d-none" name="hiddenFrame" ref="formIframe" width="0" height="0"></iframe>
      <form
        ref="quoteRequestForm"
        class="d-none"
        :action="quoteRequestTypeUrl"
        method="post"
        target="hiddenFrame"
      >
        <input type="hidden" name="comments" :value="v$.requestDetails.$model" />
        <input type="hidden" name="phone" :value="v$.telephone.$model" />
        <input type="hidden" name="email" :value="profile.email" />
        <input type="hidden" name="firstname" :value="profile.firstName" />
        <input type="hidden" name="lastname" :value="profile.lastName" />
        <input type="hidden" name="orgname" :value="profile.company" />
        <input
          v-if="profile.subscription?.country"
          type="hidden" name="country"
          :value="profile.subscription.country"
        />
      </form>
    </template>

    <template #footer>
      <v-spacer />

      <AppButton
        color="white"
        @click="closeDialog"
      >
        {{$t('common.cancel')}}
      </AppButton>

      <AppButton
        data-locator="send-request-button"
        color="primary"
        :disabled="v$.$invalid"
        :loading="quoteRequestPending"
        @click="sendRequest"
      >
        {{ $t("projects.dialogs.ProjectTypeQuoteRequestDialog.sendRequest") }}
      </AppButton>
    </template>
  </app-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import contactClosdIllustration from '@/assets/img/contact_closd.svg'
import AppPhoneField from '@/common/AppPhoneField.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import CopyrightLink from '@/common/CopyrightLink.vue'
import { SALES_PHONE_NUMBER, SALES_PHONE_NUMBER_WITH_COUNTRY_CODE } from '@/common/utils/closdInfo'
import { formFieldErrors } from '@/common/utils/form'
import { QUOTE_REQUEST_TOPICS, QUOTE_REQUEST_TOPICS_ENDPOINTS } from '@/common/utils/quoteRequestTopics'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { PROJECT_TYPE_COLORS } from '@/projects/dialogs/projectTypeColors'
import ProjectTypeFeatureHeader from '@/projects/project-types/ProjectTypeFeatureHeader.vue'
import { POST_ELECTRONIC_VAULT_QUOTE_REQUEST } from '@/store/modules/subscription/action_types'

// The form is submitted to the iframe since it's a standard submission,
// so we need to poll it to check if the form was successfully submitted
const FORM_SUCCESS_POLLING_INTERVAL = 50

const AVAILABLE_TOPICS_FOR_QUOTE = Object.values(QUOTE_REQUEST_TOPICS)

export default {
  name: 'ProjectTypeQuoteRequestDialog',
  components: {
    AppPhoneField,
    AppButton,
    AppSelect,
    CopyrightLink,
    ProjectTypeFeatureHeader,
  },
  mixins: [dialogMixin],
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      isPrivacyPolicyCheck: {
        required,
        sameAs: sameAs(true),
      },
      quoteRelatedProjectType: { required },
      requestDetails: { required },
      telephone: { required },
    }
  },
  props: {
    defaultProjectType: {
      type: String,
      required: true,
      validator (value) {
        return AVAILABLE_TOPICS_FOR_QUOTE.includes(value)
      },
    },
  },
  data () {
    return {
      SALES_PHONE_NUMBER,
      SALES_PHONE_NUMBER_WITH_COUNTRY_CODE,
      PROJECT_TYPE_COLORS,
      isPrivacyPolicyCheck: false,
      projectTypes: [],
      quoteRelatedProjectType: null,
      requestDetails: null,
      quoteRequestFormIntervalTimerId: -1,
      quoteRequestPending: false,
      telephone: '',
      contactClosdIllustration,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    ...mapGetters('user', [
      'canCurrentUserCreateDataroom',
      'canCurrentUserCreateProject',
      'isArchivingEnabled',
      'canCreateDataroomUnlimited',
    ]),
    phoneNumberErrors () {
      return formFieldErrors(
          this.v$.telephone,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.phoneNumber') })],
          ],
      )
    },
    quoteRequestTypeUrl () {
      return QUOTE_REQUEST_TOPICS_ENDPOINTS[this.quoteRelatedProjectType]
    },
  },
  created () {
    if (!this.canCurrentUserCreateProject) {
      this.projectTypes.push({ title: this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.projectTypes.project'), value: QUOTE_REQUEST_TOPICS.STD_PROJECT })
    }

    if (!this.canCurrentUserCreateDataroom || !this.canCreateDataroomUnlimited) {
      this.projectTypes.push({ title: this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.projectTypes.dataroom'), value: QUOTE_REQUEST_TOPICS.DATAROOM })
    }

    if (!this.isArchivingEnabled) {
      this.projectTypes.push({ title: this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.projectTypes.archives'), value: QUOTE_REQUEST_TOPICS.ELECTRONIC_SAFE })
    }

    this.quoteRelatedProjectType = this.defaultProjectType
    this.telephone = this.profile.telephone
  },
  beforeUnmount () {
    if (this.quoteRequestFormIntervalTimerId !== -1) {
      clearInterval(this.quoteRequestFormIntervalTimerId)
    }
  },
  methods: {
    ...mapActions('subscription', [POST_ELECTRONIC_VAULT_QUOTE_REQUEST]),
    async sendRequest () {
      const isFormValid = !this.v$.$invalid
      if (!isFormValid) {
        return
      }

      if (this.quoteRelatedProjectType === QUOTE_REQUEST_TOPICS.ELECTRONIC_SAFE) {
        await this.sendElectronicVaultQuoteRequest()
        return
      }

      try {
        this.$refs.quoteRequestForm.submit()
        this.quoteRequestPending = true
        this.quoteRequestFormIntervalTimerId = setInterval(() => {
          // If the iframe is trying to reload the page, the form has been submitted
          if (this.$refs.formIframe.contentWindow.location.hostname === window.location.hostname) {
            clearInterval(this.quoteRequestFormIntervalTimerId)
            this.onFormSubmitSuccess()
          }
        }, FORM_SUCCESS_POLLING_INTERVAL)

        // Just in case, we set an onload handler to the iframe
        // When it "loads", it means the form was submit and has finished redirecting,
        // So it is done. This unfortunately means we may wait until
        // The newly booted app from the redirection is fully loaded
        this.$refs.formIframe.onload = this.onFormSubmitSuccess

      } catch (error) {
        console.error(error)
        toast.error(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestErrorMessage'))
      }
    },
    // Electronic Vault ("ES") quote requests go through the generic Closd -> Salesforce
    // notification service instead of the legacy Pardot iframe used by DR/PR.
    async sendElectronicVaultQuoteRequest () {
      this.quoteRequestPending = true
      try {
        await this.POST_ELECTRONIC_VAULT_QUOTE_REQUEST({
          phone: this.telephone,
          message: this.requestDetails,
          privacyPolicyAccepted: this.isPrivacyPolicyCheck,
        })
        // Resets quoteRequestPending and closes the dialog, same as the DR/PR iframe path.
        this.onFormSubmitSuccess()
      } catch (error) {
        console.error(error)
        this.quoteRequestPending = false
        toast.error(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestErrorMessage'))
      }
    },
    onFormSubmitSuccess () {
      this.quoteRequestPending = false
      toast.success(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestSuccessMessage'))
      this.closeDialog()
    },
    handlePhoneChange (input, event) {
      this.telephone = event.number || input
    },
  },
}
</script>

<style scoped lang="scss">
.phone-number {
  color: #4C75B5;
  text-decoration: none;
}

.projet-type-select {
  max-width: 390px;
}

.body {
  margin: 0 45px;
}
@media (max-width: 768px) {
  .body {
    margin: 0 20px;
  }
}

.privacy-policy-check {
  margin-top: 16px;
  padding-top: 4px;

  :deep(.v-selection-control) {
    align-items: flex-start;
  }
}

.copyright {
  position: absolute;
  right: -20px;
  transform: rotate(-90deg);
}
</style>
